import { z } from 'zod';
import { matchIsValidTel } from 'mui-tel-input';
import { PatientAddress, InsuranceInfo, EmergencyContact } from '../../types/types';

// US states for validation
export const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
] as const;

// Helper function to validate E.164 phone format
const validateE164Phone = (val: string | null | undefined) => {
  if (!val || val === '') return true;
  // E.164 format: +[country code][number]
  // For US: +1XXXXXXXXXX (11 chars total)
  return /^\+[1-9]\d{1,14}$/.test(val);
};

// Address validation schema
const addressSchema = z.object({
  line1: z.string().nullable().optional(),
  line2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z
    .enum(['', ...US_STATES])
    .nullable()
    .optional(),
  zip: z
    .string()
    .nullable()
    .optional()
    .refine(val => {
      if (!val || val === '') return true;
      return /^\d{5}(-\d{4})?$/.test(val);
    }, 'ZIP code must be in format 12345 or 12345-1234'),
  phone: z
    .string()
    .nullable()
    .optional()
    .refine(validateE164Phone, 'Phone must be in E.164 format (e.g., +12125551234)'),
});

/**
 * @deprecated Insurance validation is now handled by the patient-coverages API.
 * This schema is kept for backward compatibility during the transition period.
 * New implementations should use the coverage API endpoints which have their own validation.
 */
// Insurance validation schema
const insuranceSchema = z.object({
  payer_id: z.string().nullable().optional(),
  payer_name: z.string().nullable().optional(),
  plan_name: z.string().nullable().optional(),
  policy_number: z.string().nullable().optional(),
  group_number: z.string().nullable().optional(),
  insured_name: z.string().nullable().optional(),
  insured_dob: z.string().nullable().optional(),
  insured_sex: z.enum(['Male', 'Female', '']).nullable().optional(),
  insured_address: addressSchema.nullable().optional(),
  relationship_to_insured: z
    .enum(['self', 'spouse', 'child', 'other', ''])
    .nullable()
    .optional(),
  authorization_on_file: z.boolean().nullable().optional(),
  authorization_resource_location: z.string().nullable().optional(),
});

// Emergency contact validation schema
const emergencyContactSchema = z.object({
  name: z.string().nullable().optional(),
  phone: z
    .string()
    .nullable()
    .optional()
    .refine(validateE164Phone, 'Phone must be in E.164 format (e.g., +12125551234)'),
  email: z
    .string()
    .nullable()
    .optional()
    .refine(val => {
      if (!val || val === '') return true;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(val);
    }, 'Please enter a valid email address'),
  relationship: z.string().nullable().optional(),
});

// Patient validation schema
export const patientValidationSchema = z
  .object({
    patient_id: z.string(),
    identifier: z.string().nullable().optional(),
    dob: z
      .string()
      .nullable()
      .optional()
      .refine(val => {
        if (!val) return true;
        const date = new Date(val);
        const today = new Date();
        return date <= today;
      }, 'Date of birth cannot be in the future'),
    first_name: z.string().transform(val => val || ''),
    last_name: z.string().transform(val => val || ''),
    middle_name: z.string().nullable().optional(),
    gender: z.enum(['male', 'female', 'other', '']).nullable().optional(),
    sex: z.enum(['Male', 'Female', '']).nullable().optional(),
    race: z.string().nullable().optional(),
    address: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    state: z
      .enum(['', ...US_STATES])
      .nullable()
      .optional(),
    zip: z
      .string()
      .nullable()
      .optional()
      .refine(val => {
        if (!val || val === '') return true;
        return /^\d{5}(-\d{4})?$/.test(val);
      }, 'ZIP code must be in format 12345 or 12345-1234'),
    phone: z
      .string()
      .nullable()
      .optional()
      .refine(val => {
        if (!val || val === '') return true;
        return matchIsValidTel(val);
      }, 'Please enter a valid phone number'),
    email: z
      .string()
      .nullable()
      .optional()
      .refine(val => {
        if (!val || val === '') return true;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(val);
      }, 'Please enter a valid email address'),
    payer_id: z.string().nullable().optional(),
    payer_name: z.string().nullable().optional(),
    eligibility: z.string().nullable().optional(),
    payer_relationship: z
      .enum(['self', 'spouse', 'child', 'employee', 'unknown', 'life_partner', 'dependent', ''])
      .nullable()
      .optional(),
    insurance_policy_number: z.string().nullable().optional(),
    pronouns: z.string().nullable().optional(),
    
    // New structured fields
    mobile_phone: z
      .string()
      .nullable()
      .optional()
      .refine(validateE164Phone, 'Mobile phone must be in E.164 format (e.g., +12125551234)'),
    primary_address: addressSchema.nullable().optional(),
    secondary_address: addressSchema.nullable().optional(),
    primary_insurance: insuranceSchema.nullable().optional(),
    secondary_insurance: insuranceSchema.nullable().optional(),
    tertiary_insurance: insuranceSchema.nullable().optional(),
    employment_status: z.string().nullable().optional(),
    occupation: z.string().nullable().optional(),
    patient_consent_on_file: z.boolean().nullable().optional(),
    patient_consent_resource_location: z.string().nullable().optional(),
    emergency_contact: emergencyContactSchema.nullable().optional(),
  })
  .superRefine((data, ctx) => {
    const firstNameLength = data.first_name?.length || 0;
    const lastNameLength = data.last_name?.length || 0;

    // Check if at least one name has 2+ characters
    const hasValidName = firstNameLength >= 2 || lastNameLength >= 2;

    if (!hasValidName) {
      // Only add this error if both fields are invalid
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one name field must have at least two characters',
        path: ['first_name'],
      });
    }

    // Only validate individual fields if they have content and we don't already have a valid name
    if (data.first_name && data.first_name.length > 0 && data.first_name.length < 2) {
      // Only show this error if the other field isn't valid
      if (lastNameLength < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'First name must be at least 2 characters',
          path: ['first_name'],
        });
      }
    }

    if (data.last_name && data.last_name.length > 0 && data.last_name.length < 2) {
      // Only show this error if the other field isn't valid
      if (firstNameLength < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Last name must be at least 2 characters',
          path: ['last_name'],
        });
      }
    }

    // Pattern validation - remove pattern checks for names
    // Names can now contain any characters
  });

// Type for validated patient data
export type ValidatedPatientData = z.infer<typeof patientValidationSchema>;

// Individual field validators for real-time validation
export const patientFieldValidators = {
  mobile_phone: (value: any) => {
    if (!value || value === '' || value === null || value === undefined) return { isValid: true };
    const isValid = validateE164Phone(String(value));
    return {
      isValid,
      error: isValid ? undefined : 'Mobile phone must be in E.164 format (e.g., +12125551234)',
    };
  },
  e164_phone: (value: any) => {
    if (!value || value === '' || value === null || value === undefined) return { isValid: true };
    const isValid = validateE164Phone(String(value));
    return {
      isValid,
      error: isValid ? undefined : 'Phone must be in E.164 format (e.g., +12125551234)',
    };
  },
  phone: (value: any) => {
    if (!value || value === '' || value === null || value === undefined) return { isValid: true };
    const isValid = matchIsValidTel(String(value));
    return {
      isValid,
      error: isValid ? undefined : 'Please enter a valid phone number',
    };
  },
  email: (value: any) => {
    if (!value || value === '' || value === null || value === undefined) return { isValid: true };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(String(value));
    return {
      isValid,
      error: isValid ? undefined : 'Please enter a valid email address',
    };
  },
  zip: (value: any) => {
    if (!value || value === '' || value === null || value === undefined) return { isValid: true };
    const zipRegex = /^\d{5}(-\d{4})?$/;
    const isValid = zipRegex.test(String(value));
    return {
      isValid,
      error: isValid ? undefined : 'ZIP code must be in format 12345 or 12345-1234',
    };
  },
  state: (value: any) => {
    if (!value || value === '' || value === null || value === undefined) return { isValid: true };
    const isValid = US_STATES.includes(String(value) as any);
    return {
      isValid,
      error: isValid ? undefined : 'Please enter a valid US state abbreviation',
    };
  },
  dob: (value: any) => {
    if (!value || value === '' || value === null || value === undefined) return { isValid: true };
    const date = new Date(String(value));
    const today = new Date();
    const isValid = date <= today;
    return {
      isValid,
      error: isValid ? undefined : 'Date of birth cannot be in the future',
    };
  },
  name: (value: any, fieldName: string) => {
    const isNameField = fieldName === 'first name' || fieldName === 'last name';

    // For name fields, allow empty but if provided must be at least 2 chars
    if (!value || value === '' || value === null || value === undefined) {
      // Empty is allowed for all name fields
      return { isValid: true };
    }

    const strValue = String(value);

    // Check length requirement for first/last names only
    if (isNameField && strValue.length < 2) {
      return {
        isValid: false,
        error: `${fieldName} must be at least 2 characters`,
      };
    }

    // No pattern restrictions - allow any characters
    return { isValid: true };
  },
};
